import { IVec2 } from "@base/math/Vec2";
import { IVec3 } from "@base/math/Vec3";
import { ReducerHelper } from "@base/resi/ReducerHelper";
import { IdGen } from "@base/util/IdGen";
import { ImmHelper } from "@base/util/Immutable";
import { Util } from "@base/util/Util";
import { ConfiguratorItemData, getItemRecordForItem, IVariantData3d } from "@biz/definitions/IVariantData3d";
import { ILayerData, LockState } from "@biz/definitions/LayerDefinitions";
import { ILoungeItem } from "@biz/definitions/LoungeDefinitions";
import { IGroupData } from "@biz/definitions/ProductDefinitions";
import { SelectionData } from "@biz/definitions/SelectionDefinitions";
import { IPatchData, PoleId, PolePositionType } from "@biz/definitions/SunSailDefinitions";
import { VariantData3d } from "@biz/definitions/VariantData3d";
import { TrackJointType } from "@biz/items/curtain/CurtainDefinitions";
import { CurtainLogic } from "@biz/items/curtain/CurtainLogic";
import { CurtainReducer } from "@biz/items/curtain/CurtainReducer";
import { FenceReducer } from "@biz/items/fence/FenceReducer";
import { GroundReducer } from "@biz/items/ground/GroundReducer";
import { LoungeReducer } from "@biz/items/lounge/LoungeReducer";
import { TexInfoReducer } from "@biz/items/reducers/TexInfoReducer";
import { SandyLogic } from "@biz/items/sunSail/logic/SandyLogic";
import { Variant3dClone } from "@biz/items/Variant3dClone";
import { WallReducer } from "@biz/items/wall/WallReducer";
import { GroupHelper } from "@biz/logic/GroupHelper";
import { IUserFileInfo, VariantDataType } from "@biz/userProjects/new/IVariant";
import pick from "lodash/pick";

export class SelectionHelper {
  static optimizeSelection(selection: IVariantData3d["selection"]): IVariantData3d["selection"] {
    let newSelection: IVariantData3d["selection"];
    if (Array.isArray(selection)) {
      if (selection.length > 1)
        newSelection = selection;
      else if (selection.length === 1)
        newSelection = selection[0];
    } else {
      newSelection = selection;
    }
    return newSelection;
  }

  static toSelectionArray(selection: IVariantData3d["selection"]): SelectionData[] {
    return Array.isArray(selection) ? [...selection] : (selection ? [selection] : []); // immutable
  }

  static mergeSelection(a: IVariantData3d["selection"], b: IVariantData3d["selection"]): IVariantData3d["selection"] {
    const ret: SelectionData[] = SelectionHelper.toSelectionArray(a);
    const ba = SelectionHelper.toSelectionArray(b)
    for (const x of ba) {
      const index = ret.findIndex((item) => item.id === x.id);
      if (index === -1) {
        ret.unshift(x);
      } else {
        ret.splice(index, 1);
        // ret[index] = x;
      }
    }

    return ret;
  }
}

function updateLayerItem(old: IVariantData3d, layerId: string, updateFn: (ld: ILayerData | undefined) => ILayerData): IVariantData3d {
  const oldLayer = old?.layers?.[layerId];
  return ImmHelper.setProperty(old, "layers", layerId, updateFn(oldLayer));
}


export const VariantCommonReducer = {
  ...ReducerHelper.createReducerHelper<VariantDataType>(),
  addUserFile(old: VariantDataType, file: IUserFileInfo): VariantDataType {
    return {
      ...old,
      userFiles: {
        ...old.userFiles,
        [file.id]: file
      }
    }
  },
  removeUserFile(old: VariantDataType, fileId: string): VariantDataType {
    return {
      ...old,
      userFiles: old.userFiles ? ImmHelper.deleteProperty(old.userFiles, fileId) : undefined
    }
  },
}

export const Variant3dReducer = {
  ...ReducerHelper.createReducerHelper<IVariantData3d>(),
  setName(old: IVariantData3d, id: string, name: string): IVariantData3d {
    return VariantData3d.updateConfiguratorItem(old, id, (item) => ImmHelper.setProperty(item, "name", name));
  },
  add(old: IVariantData3d, item: ConfiguratorItemData, options?: { withSelect?: boolean }): IVariantData3d {
    const { withSelect = true } = options || {};
    const key = getItemRecordForItem(item);
    if (!key)
      return old;

    const byId = { ...old[key]?.byId || {}, [item.id]: item };
    old = { ...old, [key]: { byId, ids: Object.keys(byId) } };

    if (item.itemType === "group") {
      old = GroupHelper.setGroupIdForItems(old, item.children, item.id);
    }
    if (item.parentId) {
      old = GroupHelper.addToChildList(old, item.parentId, item.id);
    }

    if (withSelect) {
      return Variant3dReducer.select(old, {
        type: item.itemType,
        id: item.id,
      }, { sideTab: "properties" })
    } else {
      return old;
    }
  },
  changeGroup: (old: IVariantData3d, sourceId: string | undefined, targetId: string | undefined, options?: { withSelect?: boolean }): IVariantData3d => {
    if (sourceId !== undefined && targetId !== undefined) {
      const item = VariantData3d.findVariantData3dItem(old, sourceId);
      if (item) {
        if (item.parentId)
          old = GroupHelper.removeFromChildList(old, item.parentId, item.id)
        old = GroupHelper.addToChildList(old, targetId, item.id);
        old = GroupHelper.setGroupIdForItem(old, item.id, targetId);
        if (options?.withSelect) {
          old = Variant3dReducer.select(old, {
            type: "group",
            id: targetId,
          }, { sideTab: "properties" })
        }
      }
    }
    return old;
  },
  groupItems(old: IVariantData3d, selectionIds: string[], options?: { withSelect?: boolean }): IVariantData3d {
    const groupId = selectionIds.length > 0 ? VariantData3d.findVariantData3dItem(old, selectionIds[0])?.parentId : undefined
    const item: IGroupData = { id: IdGen.unsecure.get(), itemType: "group", children: selectionIds, parentId: groupId }
    return Variant3dReducer.add(old, item, options);
  },
  unGroupItems(old: IVariantData3d, idOrIds: string | string[], _options?: { withSelect?: boolean }): IVariantData3d {
    const selectionIds = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
    const group = GroupHelper.getGroup(old, selectionIds);
    if (group.id && group.same) {
      old = GroupHelper.removeFromChildList(old, group.id, selectionIds)
      const groupItem = VariantData3d.findVariantData3dItem(old, group.id);
      old = GroupHelper.setGroupIdForItems(old, selectionIds, groupItem?.parentId)
    }
    return old;
  },
  removeGroup(old: IVariantData3d, groupId: string): IVariantData3d {
    const item = VariantData3d.findVariantData3dItem(old, groupId);
    if (item?.itemType === "group") {
      const children = item.children;
      if (children) {
        old = GroupHelper.setChildList(old, groupId, []);
        old = GroupHelper.setGroupIdForItems(old, children, item.parentId)
        return Variant3dReducer.remove(old, groupId);
      }
    }
    return old;
  },

  remove: (old: IVariantData3d, id: string | string[]): IVariantData3d => {
    const ids = Array.isArray(id) ? id : [id];
    if (ids.length === 0)
      return old;

    let index = 0;
    while (index < ids.length) {
      // add group childrens to list
      const item = VariantData3d.findVariantData3dItem(old, ids[index]);
      if (item?.itemType === "group" && item.children?.length) {
        ids.push(...item.children);
      }
      // remove item groups childlist
      if (item?.parentId) {
        old = GroupHelper.removeFromChildList(old, item.parentId, item.id)
      }
      index++;
    }

    const records = VariantData3d.getRecords();
    for (const r of records) {
      const oldById = old[r]?.byId;
      if (oldById) {
        let byId = oldById;
        for (const id of ids) {
          if (byId[id]) {
            byId = ImmHelper.deleteProperty(byId, id);
          }
        }
        if (byId !== oldById) {
          old = ImmHelper.setProperty(old, r, "byId", byId)
          old = ImmHelper.setProperty(old, r, "ids", Object.keys(byId))
        }
      }
    }
    return Variant3dReducer.deselect(old);
  },
  cloneItem: (old: IVariantData3d, idOrIds: string | string[]): IVariantData3d => {
    const ids = Util.ensureArray(idOrIds);

    const clonedItems: ConfiguratorItemData[] = [];
    const clonedChildren: ConfiguratorItemData[] = [];
    for (const id of ids) {
      const cloned = Variant3dClone.cloneItem(old, id);
      if (cloned) {
        clonedItems.push(cloned.item);
        if (cloned.children)
          clonedChildren.push(...cloned.children);
      }
    }
    if (clonedItems) {
      for (const clonedChild of clonedChildren)
        old = Variant3dReducer.add(old, clonedChild, { withSelect: false });
      for (const cloned of clonedItems)
        old = Variant3dReducer.add(old, cloned, { withSelect: false });
      return Variant3dReducer.select(old, clonedItems.map((item) => ({
        type: item.itemType,
        id: item.id,
      })), { sideTab: "properties" })
    }
    return old;
  },
  select(old: IVariantData3d, selection: SelectionData | SelectionData[] | undefined, options?: { sideTab?: string, multi?: boolean }): IVariantData3d {

    if (options?.multi) {
      selection = SelectionHelper.mergeSelection(old.selection, selection);
    }

    const newSelection = SelectionHelper.optimizeSelection(selection);

    return {
      ...old,
      selection: newSelection,
      sideTab: (old.sideTab && selection && options?.sideTab) || old.sideTab
    }
  },
  selectById(old: IVariantData3d, id: string | undefined, options?: { sideTab?: string; multi?: boolean; useGroup?: boolean }): IVariantData3d {
    let item = id ? VariantData3d.findVariantData3dItem(old, id) : undefined;
    if (options?.useGroup) {
      while (item?.parentId && item?.parentId !== "root") {
        item = VariantData3d.findVariantData3dItem(old, item?.parentId);
      }
    }
    return Variant3dReducer.select(old, item ? { id: item.id, type: item.itemType } : undefined, options);
  },
  deselect(old: IVariantData3d): IVariantData3d {
    return ImmHelper.setProperty(old, "selection", undefined);
  },
  setSideTab(old: IVariantData3d, sideTab: string): IVariantData3d {
    return ImmHelper.setProperty(old, "sideTab", sideTab)
  },
  layerToggleHidden(old: IVariantData3d, layerId: string): IVariantData3d {
    return updateLayerItem(old, layerId, (ld) => ({ ...ld, hidden: !ld?.hidden }));
  },
  layerToggleLocked(old: IVariantData3d, layerId: string): IVariantData3d {
    return updateLayerItem(old, layerId, (ld) => ({ ...ld, locked: GroupHelper.toggleLock(ld?.locked) }));
  },
  layerToggleOrderable(old: IVariantData3d, layerId: string): IVariantData3d {
    return updateLayerItem(old, layerId, (ld) => ({ ...ld, notOrderable: !ld?.notOrderable }));
  },
  layerSetLocked(old: IVariantData3d, layerId: string, locked: LockState): IVariantData3d {
    return updateLayerItem(old, layerId, (ld) => ({ ...ld, locked }));
  },
  toggleLocked(old: IVariantData3d, id: string): IVariantData3d {
    return VariantData3d.updateConfiguratorItem(old, id, (item) => ({ ...item, locked: GroupHelper.toggleLock(item?.locked) }))
  },
  toggleHidden(old: IVariantData3d, id: string): IVariantData3d {
    return VariantData3d.updateConfiguratorItem(old, id, (item) => ({ ...item, hidden: !item.hidden }))
  },
  toggleOrderable(old: IVariantData3d, id: string): IVariantData3d {
    return VariantData3d.updateConfiguratorItem(old, id, (item) => ({ ...item, notOrderable: !item.notOrderable }))
  },
  setPresentationMode: (old: IVariantData3d, flag: boolean): IVariantData3d => {
    if (flag && !old.settings.presentationMode) {
      const newSettings = ImmHelper.update(old.settings, {
        presentationMode: {
          settings: pick(old.settings, "animationMode", "shadowMode", "showGrid", "hideErrors"),
          products: old.layers?.products?.locked,
          surroundings: old.layers?.surroundings?.locked,
        },
        hideErrors: true
      });
      let newState = ImmHelper.setProperty(old, "settings", newSettings);
      newState = Variant3dReducer.layerSetLocked(newState, "products", "lockedTransform")
      newState = Variant3dReducer.layerSetLocked(newState, "surroundings", "locked")
      return newState;
    }
    if (!flag && old.settings.presentationMode?.settings) {
      let newSettings = ImmHelper.update(old.settings, old.settings.presentationMode.settings)
      newSettings = ImmHelper.setProperty(newSettings, "hideErrors", false)
      newSettings = ImmHelper.setProperty(newSettings, "presentationMode", undefined);
      newSettings = ImmHelper.setProperty(newSettings, "animationMode", "off");
      let newState = ImmHelper.setProperty(old, "settings", newSettings);
      newState = Variant3dReducer.layerSetLocked(newState, "products", old.settings.presentationMode.products || false)
      newState = Variant3dReducer.layerSetLocked(newState, "surroundings", old.settings.presentationMode.surroundings || false)
      return newState;
    }
    if (!flag)
      return ImmHelper.setProperty(old, "settings", "presentationMode", undefined)
    return old;
  },
  addGroundCorner(old: IVariantData3d, id: string, cornerIndex: number, p?: IVec3): IVariantData3d {
    const oldItem = old.grounds.byId[id];
    const newItem = GroundReducer.addCorner(oldItem, cornerIndex, p);
    old = ImmHelper.setProperty(old, "grounds", "byId", id, newItem);

    const selectedItem = VariantData3d.getSelected(old, { type: "ground", id, cornerIndex }, { strict: true });
    old = VariantData3d.replaceSelection(old, selectedItem, { type: "ground", id, cornerIndex: cornerIndex + 1 });

    return old;
  },
  removeGroundCorner(old: IVariantData3d, id: string, cornerIndex: number): IVariantData3d {
    const newItem = GroundReducer.removeCorner(old.grounds.byId[id], cornerIndex);
    old = ImmHelper.setProperty(old, "grounds", "byId", id, newItem);

    const selectedItem = VariantData3d.getSelected(old, { type: "ground", id, cornerIndex }, { strict: true });
    old = VariantData3d.replaceSelection(old, selectedItem, { type: "ground", id, cornerIndex: (cornerIndex + newItem.corners.length - 1) % newItem.corners.length });
    return old;
  },
  removeFenceCorner(old: IVariantData3d, id: string, cornerIndex: number): IVariantData3d {
    const newItem = FenceReducer.removeCorner(old.fences.byId[id], cornerIndex);
    old = ImmHelper.setProperty(old, "fences", "byId", id, newItem);

    const selectedItem = VariantData3d.getSelected(old, { type: "fence", id, cornerIndex }, { strict: true });
    old = VariantData3d.replaceSelection(old, selectedItem, { type: "fence", id, cornerIndex: (cornerIndex + newItem.corners.length - 1) % newItem.corners.length });

    return old;
  },
  removeWallCorner(old: IVariantData3d, id: string, cornerIndex: number): IVariantData3d {
    const newItem = WallReducer.removeCorner(old.walls.byId[id], cornerIndex);
    old = ImmHelper.setProperty(old, "walls", "byId", id, newItem);

    const selectedItem = VariantData3d.getSelected(old, { type: "wall", id, cornerIndex }, { strict: true });
    old = VariantData3d.replaceSelection(old, selectedItem, { type: "wall", id, cornerIndex: (cornerIndex + newItem.corners.length - 1) % newItem.corners.length });

    return old;
  },
  removeCurtainTrackCorner(old: IVariantData3d, id: string, trackIndex: number, cornerIndex: number): IVariantData3d {
    const newItem = CurtainReducer.removeTrackPoint(old.curtains.byId[id], trackIndex, cornerIndex)
    old = ImmHelper.setProperty(old, "curtains", "byId", id, newItem);

    const selectedItem = VariantData3d.getSelected(old, { type: "curtain", id, trackIndex, cornerIndex }, { strict: true });
    old = VariantData3d.replaceSelection(old, selectedItem, { type: "curtain", id, trackIndex, cornerIndex: (cornerIndex + newItem.tracks[trackIndex].corners.length - 1) % newItem.tracks[trackIndex].corners.length });

    return old;
  },
  addCurtainTrackCorner(old: IVariantData3d, id: string, trackIndex: number, cornerIndex: number | undefined, jointType: TrackJointType, dir: "left" | "right" | "straight"): IVariantData3d {
    const newItem = CurtainReducer.addTrackPoint(old.curtains.byId[id], trackIndex, cornerIndex, jointType, dir)
    const newSelection: SelectionData = {
      type: "curtain",
      id,
      trackIndex,
      cornerIndex: cornerIndex === undefined ? newItem.tracks[trackIndex].corners.length - 1 : cornerIndex + 1
    }
    old = ImmHelper.setProperty(old, "curtains", "byId", id, newItem);
    old = ImmHelper.setProperty(old, "selection", newSelection);
    return old;
  },
  addCurtainTrackCloth(old: IVariantData3d, id: string, trackIndex: number, options: { cornerIndex?: number, clothIndex?: number }): IVariantData3d {

    const _cornerIndex = options?.cornerIndex !== undefined ? options.cornerIndex : (options.clothIndex !== undefined ? old.curtains.byId[id].tracks[trackIndex].clothes[options.clothIndex].anchorPoint : undefined);
    const cornerIndex = _cornerIndex !== undefined ? _cornerIndex : 0;

    const newItem = CurtainReducer.addCloth(old.curtains.byId[id], trackIndex, { anchorPoint: cornerIndex, align: cornerIndex < CurtainLogic.getCornerCount(old.curtains.byId[id], trackIndex) - 1 ? "left" : "right" });
    old = ImmHelper.setProperty(old, "curtains", "byId", id, newItem);
    old = ImmHelper.setProperty(old, "selection", { id, type: "curtain", trackIndex, clothIndex: old.curtains.byId[id].tracks[trackIndex].clothes.length - 1 });
    return old;
  },
  removeCurtainTrackCloth(old: IVariantData3d, id: string, trackIndex: number, clothIndex: number): IVariantData3d {
    const newItem = CurtainReducer.removeCloth(old.curtains.byId[id], trackIndex, clothIndex)
    old = ImmHelper.setProperty(old, "curtains", "byId", id, newItem);


    const selectedItem = VariantData3d.getSelected(old, { type: "curtain", id, trackIndex, clothIndex }, { strict: true });
    if (selectedItem) {
      const clothCount = newItem.tracks[trackIndex].clothes.length;
      old = VariantData3d.replaceSelection(old, selectedItem, { type: "curtain", id, trackIndex, clothIndex: clothCount ? ((clothIndex + clothCount - 1) % clothCount) : undefined });
    }

    return old;
  },
  removeCurtainTrack(old: IVariantData3d, id: string, trackIndex: number): IVariantData3d {
    const newItem = CurtainReducer.removeTrack(old.curtains.byId[id], trackIndex)
    old = ImmHelper.setProperty(old, "curtains", "byId", id, newItem);

    const selectedItem = VariantData3d.getSelected(old, { type: "curtain", id, trackIndex });
    if (selectedItem) {
      const trackCount = newItem.tracks.length;
      const newSelection: SelectionData = {
        type: "curtain",
        id,
        trackIndex: trackCount ? ((trackIndex + trackCount - 1) % trackCount) : undefined,
      }
      old = ImmHelper.setProperty(old, "selection", newSelection);
    }
    return old;
  },
  setCardinalDirection(old: IVariantData3d, cardinalDirection: number): IVariantData3d {
    return {
      ...old,
      settings: {
        ...old.settings,
        cardinalDirection
      }
    }
  },
  addLoungeItem(old: IVariantData3d, id: string, index: number, item: ILoungeItem): IVariantData3d {
    const oldItem = VariantData3d.getTyped("lounge", old, id)!;
    if (index < 0)
      index = oldItem.chains[0].items.length + index + 1;
    const newItem = LoungeReducer.addItem(oldItem, index, item);
    old = ImmHelper.setProperty(old, "lounges", "byId", id, newItem);
    old = ImmHelper.setProperty(old, "selection", { type: "lounge", id, itemIndex: index })
    return old;
  },
  removeLoungeItem(old: IVariantData3d, id: string, index: number): IVariantData3d {
    const oldItem = VariantData3d.getTyped("lounge", old, id)!;

    const newItem = LoungeReducer.removeItem(oldItem, index);
    old = ImmHelper.setProperty(old, "lounges", "byId", id, newItem);

    const selection = VariantData3d.getSelected(old, { type: "lounge", id, itemIndex: index });
    if (selection) {
      const itemCount = newItem.chains[0].items.length;
      old = ImmHelper.setProperty(old, "selection", {
        type: "lounge",
        id,
        itemIndex: itemCount ? ((index + itemCount - 1) % itemCount) : undefined,
      })
    }
    return old;
  },
  setTextureArea(old: IVariantData3d, id: string, textureArea: IVec2[]) {
    const item = VariantData3d.findVariantData3dItem(old, id);
    if (!item)
      return old;
    if ("texInfo" in item) {
      const newItem = TexInfoReducer.setTextureArea(item.texInfo, textureArea);
      return {
        ...old,
        texInfo: newItem
      }
    }
    return old;
  },
  addSandyPole(old: IVariantData3d, id: string, poleIdOrPositionType: PoleId | PolePositionType): IVariantData3d {
    const oldSunSail = old.sunSails.byId[id];
    const changed = SandyLogic.addSandyPole(oldSunSail, poleIdOrPositionType)
    if (oldSunSail === changed.sunSailData)
      return old;
    return {
      ...old,
      sunSails: ImmHelper.setProperty(old.sunSails, "byId", id, changed.sunSailData),
      selection: { type: "sunSail", id, poleId: changed.selectedPoleId }
    }
  },
  removeSandyPole(old: IVariantData3d, id: string, poleId: PoleId): IVariantData3d {
    const oldSunSail = old.sunSails.byId[id];
    const changed = SandyLogic.removeSandyPole(oldSunSail, poleId)
    if (oldSunSail === changed.sunSailData)
      return old;
    return {
      ...old,
      sunSails: ImmHelper.setProperty(old.sunSails, "byId", id, changed.sunSailData),
      selection: changed.selectedPoleId ? {
        type: "sunSail", id, poleId: changed.selectedPoleId
      } : undefined
    }
  },
  addPatch(old: IVariantData3d, id: string, patch: IPatchData | undefined): IVariantData3d {
    const oldSunSail = old.sunSails.byId[id];
    const changed = SandyLogic.addPatch(oldSunSail, patch);
    if (oldSunSail === changed)
      return old;

    return {
      ...old,
      sunSails: ImmHelper.setProperty(old.sunSails, "byId", id, changed),
      selection: {
        type: "sunSail", id, patchIndex: changed.patches!.length - 1
      }
    }
  },
  removePatch(old: IVariantData3d, id: string, patchIndex: number): IVariantData3d {
    const oldSunSail = old.sunSails.byId[id];
    const changed = SandyLogic.removePatch(oldSunSail, patchIndex)
    if (oldSunSail === changed.sunSailData)
      return old;
    return {
      ...old,
      sunSails: ImmHelper.setProperty(old.sunSails, "byId", id, changed.sunSailData),
      selection: changed.selectedPatchIndex ? {
        type: "sunSail", id, patchIndex: changed.selectedPatchIndex
      } : undefined
    }
  }
}